import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, ShoppingCart, Package, ShoppingBag, Users,
  Truck, Wallet, BarChart3, Settings, Menu, X, LogOut,
  FileText, CreditCard, Database, Tag, UserCog, Shield, Store
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'gerente', 'cajero'] },
    { path: '/ventas', icon: ShoppingCart, label: 'Ventas', roles: ['admin', 'gerente', 'cajero', 'vendedor'] },
    { path: '/mayorista', icon: Store, label: 'Mayorista', roles: ['admin', 'gerente', 'vendedor'] },
    { path: '/productos', icon: Package, label: 'Productos', roles: ['admin', 'gerente', 'inventario'] },
    { path: '/categorias', icon: Tag, label: 'Categorías', roles: ['admin', 'gerente', 'inventario'] },
    { path: '/compras', icon: ShoppingBag, label: 'Compras', roles: ['admin', 'gerente'] },
    { path: '/cotizaciones', icon: FileText, label: 'Cotizaciones', roles: ['admin', 'gerente', 'vendedor'] },
    { path: '/clientes', icon: Users, label: 'Clientes', roles: ['admin', 'gerente', 'vendedor'] },
    { path: '/proveedores', icon: Truck, label: 'Proveedores', roles: ['admin', 'gerente'] },
    { path: '/credito', icon: CreditCard, label: 'Crédito', roles: ['admin', 'gerente'] },
    { path: '/caja', icon: Wallet, label: 'Caja', roles: ['admin', 'gerente', 'cajero'] },
    { path: '/reportes', icon: BarChart3, label: 'Reportes', roles: ['admin', 'gerente'] },
    { path: '/backup', icon: Database, label: 'Backup', roles: ['admin'] },
    { path: '/permisos', icon: Shield, label: 'Usuarios y Permisos', roles: ['admin'] },
    { path: '/configuracion', icon: Settings, label: 'Configuración', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.rol)
  );

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar para móvil */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div 
          className={`absolute inset-0 bg-gray-600 transition-opacity ${sidebarOpen ? 'opacity-75' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        ></div>
        
        <div className={`absolute top-0 left-0 w-64 h-full bg-white shadow-xl transform transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold text-primary-600">Sistema POS</h2>
            <button onClick={() => setSidebarOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <nav className="p-4">
            {filteredMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Sidebar para desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r overflow-y-auto">
          <div className="p-4 border-b">
            <h2 className="text-xl font-bold text-primary-600">Sistema POS</h2>
            <p className="text-sm text-gray-600">{user?.sucursal_nombre || 'Sucursal Principal'}</p>
          </div>
          
          <nav className="flex-1 p-4">
            {filteredMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold">
                  {user?.nombre_completo?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{user?.nombre_completo}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.rol}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Header móvil */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-primary-600">Sistema POS</h1>
          <button onClick={logout} className="text-red-600">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="lg:pl-64">
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
